import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, X, Check, Loader2, Volume2, AlertTriangle, Beaker, Apple } from 'lucide-react';
import { foodAPI, parenteralAPI } from '../services/api';
import { parseVoiceInput, matchFoodsFromDatabase } from '../utils/voiceParser';
import { formatNumber } from '../utils/numberFormat';

// Check browser support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const isSpeechSupported = !!SpeechRecognition;

// Module-level caches strictly isolated by type
let cachedFoodItems = null;
let cachedParenteralItems = null;

export default function VoiceRecallInput({ type = 'oral', onAddItems, className = '' }) {
  const isParenteral = type === 'parenteral';

  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [parsedItems, setParsedItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState({}); // itemIndex → { food, matches }
  const [phase, setPhase] = useState('idle'); // idle | listening | review | done
  const [error, setError] = useState('');

  const recognitionRef = useRef(null);

  // Load appropriate database strictly isolated by isParenteral
  useEffect(() => {
    if (!isOpen) return;

    if (isParenteral) {
      if (cachedParenteralItems && cachedParenteralItems.length > 0) {
        setAllItems(cachedParenteralItems);
      } else {
        setLoadingItems(true);
        parenteralAPI.getAll()
          .then(data => {
            const list = data || [];
            cachedParenteralItems = list;
            setAllItems(list);
          })
          .catch(err => console.error('VoiceInput: failed to load parenteral items', err))
          .finally(() => setLoadingItems(false));
      }
    } else {
      if (cachedFoodItems && cachedFoodItems.length > 0) {
        setAllItems(cachedFoodItems);
      } else {
        setLoadingItems(true);
        foodAPI.getAll()
          .then(data => {
            const list = data || [];
            cachedFoodItems = list;
            setAllItems(list);
          })
          .catch(err => console.error('VoiceInput: failed to load food items', err))
          .finally(() => setLoadingItems(false));
      }
    }
  }, [isOpen, isParenteral]);

  // Parse transcript whenever it changes and items are loaded
  useEffect(() => {
    if (!transcript || allItems.length === 0) return;
    const parsed = parseVoiceInput(transcript);
    setParsedItems(parsed);
    // Auto-select best match for each parsed item
    const autoSelected = {};
    parsed.forEach((item, idx) => {
      const matches = matchFoodsFromDatabase(item.name, allItems, 5);
      if (matches.length > 0) autoSelected[idx] = { food: matches[0], matches };
      else autoSelected[idx] = { food: null, matches: [] };
    });
    setSelectedMatches(autoSelected);
  }, [transcript, allItems]);

  const startListening = useCallback(() => {
    if (!isSpeechSupported) {
      setError('Browser Anda tidak mendukung input suara. Gunakan Chrome atau Edge terbaru.');
      return;
    }
    setError('');
    setTranscript('');
    setInterimTranscript('');
    setParsedItems([]);
    setSelectedMatches({});
    setPhase('listening');

    const recognition = new SpeechRecognition();
    recognition.lang = 'id-ID';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = true;

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t + ' ';
        else interim += t;
      }
      if (final) setTranscript(prev => (prev + ' ' + final).trim());
      setInterimTranscript(interim);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        setError('Akses mikrofon ditolak. Izinkan akses mikrofon di browser Anda.');
      } else if (event.error === 'network') {
        setError('Tidak ada koneksi internet. Input suara membutuhkan internet.');
      } else {
        setError(`Error: ${event.error}`);
      }
      setIsListening(false);
      setPhase('idle');
    };

    recognition.onend = () => {
      setIsListening(false);
      if (phase === 'listening') {
        setPhase(prev => (prev === 'listening' ? 'review' : prev));
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [phase]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimTranscript('');
    setPhase('review');
  }, []);

  const handleConfirm = () => {
    const itemsToAdd = [];

    parsedItems.forEach((parsed, idx) => {
      const match = selectedMatches[idx];
      if (!match || !match.food) return;
      const product = match.food;
      const qty = parsed.quantity || 1;

      if (isParenteral) {
        itemsToAdd.push({
          item_id: product.id || product._id,
          name: product.name,
          quantity: qty,
          unit: product.unit || parsed.unit || 'botol',
          energy_kcal: (product.macronutrients?.energy_kcal || 0) * qty,
          protein_g: (product.macronutrients?.protein_g || 0) * qty,
          carbohydrate_g: (product.macronutrients?.carbohydrate_g || 0) * qty,
          fat_g: (product.macronutrients?.fat_g || 0) * qty,
          carb_factor: product.factors?.carb_factor || 4.0,
          fat_factor: product.factors?.fat_factor || 9.0,
          sodium_mmol: (product.electrolytes?.sodium_mmol || 0) * qty,
          potassium_mmol: (product.electrolytes?.potassium_mmol || 0) * qty,
          calcium_mmol: (product.electrolytes?.calcium_mmol || 0) * qty,
          chloride_mmol: (product.electrolytes?.chloride_mmol || 0) * qty,
          magnesium_mmol: (product.electrolytes?.magnesium_mmol || 0) * qty,
          phosphate_mmol: (product.electrolytes?.phosphate_mmol || 0) * qty,
          lactate_mmol: (product.other?.lactate_mmol || 0) * qty,
          acetate_meq: (product.other?.acetate_meq || 0) * qty,
          nitrogen_g: (product.other?.nitrogen_g || 0) * qty,
        });
      } else {
        itemsToAdd.push({
          item_id: product.id,
          name: product.name,
          quantity: qty,
          unit: product.unit || parsed.unit,
          energy_kcal: (product.macronutrients?.energy_kcal || 0) * qty,
          protein_g: (product.macronutrients?.protein_g || 0) * qty,
          carbohydrate_g: (product.macronutrients?.carbohydrate_g || 0) * qty,
          fat_g: (product.macronutrients?.fat_g || 0) * qty,
          fiber_g: (product.macronutrients?.fiber_g || 0) * qty,
          sodium_mg: (product.electrolytes?.sodium_mg || 0) * qty,
          potassium_mg: (product.electrolytes?.potassium_mg || 0) * qty,
          chloride_mg: (product.electrolytes?.chloride_mg || 0) * qty,
          omega3_mg: (product.fatty_acids?.omega3_mg || 0) * qty,
          omega6_mg: (product.fatty_acids?.omega6_mg || 0) * qty,
          vitamin_a_mcg: (product.vitamins?.vitamin_a_mcg || 0) * qty,
          vitamin_d3_mcg: (product.vitamins?.vitamin_d3_mcg || 0) * qty,
          vitamin_e_mcg: (product.vitamins?.vitamin_e_mcg || 0) * qty,
          vitamin_k_mcg: (product.vitamins?.vitamin_k_mcg || 0) * qty,
          vitamin_b1_mg: (product.vitamins?.vitamin_b1_mg || 0) * qty,
          vitamin_b2_mg: (product.vitamins?.vitamin_b2_mg || 0) * qty,
          vitamin_b3_mg: (product.vitamins?.vitamin_b3_mg || 0) * qty,
          vitamin_b5_mg: (product.vitamins?.vitamin_b5_mg || 0) * qty,
          vitamin_b6_mg: (product.vitamins?.vitamin_b6_mg || 0) * qty,
          vitamin_b12_mcg: (product.vitamins?.vitamin_b12_mcg || 0) * qty,
          folic_acid_mcg: (product.vitamins?.folic_acid_mcg || 0) * qty,
          biotin_mcg: (product.vitamins?.biotin_mcg || 0) * qty,
          choline_mg: (product.vitamins?.choline_mg || 0) * qty,
          vitamin_c_mg: (product.vitamins?.vitamin_c_mg || 0) * qty,
          calcium_mg: (product.minerals?.calcium_mg || 0) * qty,
          phosphorus_mg: (product.minerals?.phosphorus_mg || 0) * qty,
          magnesium_mg: (product.minerals?.magnesium_mg || 0) * qty,
          iron_mg: (product.minerals?.iron_mg || 0) * qty,
          iodine_mcg: (product.minerals?.iodine_mcg || 0) * qty,
          zinc_mg: (product.minerals?.zinc_mg || 0) * qty,
          selenium_mcg: (product.minerals?.selenium_mcg || 0) * qty,
          manganese_mg: (product.minerals?.manganese_mg || 0) * qty,
          fluoride_mg: (product.minerals?.fluoride_mg || 0) * qty,
          chromium_mcg: (product.minerals?.chromium_mcg || 0) * qty,
          copper_mg: (product.minerals?.copper_mg || 0) * qty,
        });
      }
    });

    if (itemsToAdd.length > 0) {
      onAddItems(itemsToAdd);
    }

    handleClose();
  };

  const handleClose = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsOpen(false);
    setIsListening(false);
    setTranscript('');
    setInterimTranscript('');
    setParsedItems([]);
    setSelectedMatches({});
    setPhase('idle');
    setError('');
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTranscript('');
    setInterimTranscript('');
    setParsedItems([]);
    setSelectedMatches({});
    setPhase('idle');
    setError('');
  };

  const setMatchForItem = (itemIdx, item) => {
    setSelectedMatches(prev => ({
      ...prev,
      [itemIdx]: { ...prev[itemIdx], food: item }
    }));
  };

  const updateQty = (itemIdx, qty) => {
    setParsedItems(prev => prev.map((item, i) => (i === itemIdx ? { ...item, quantity: qty } : item)));
  };

  const removeItem = (itemIdx) => {
    setParsedItems(prev => prev.filter((_, i) => i !== itemIdx));
    setSelectedMatches(prev => {
      const updated = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = parseInt(k, 10);
        if (ki < itemIdx) updated[ki] = v;
        else if (ki > itemIdx) updated[ki - 1] = v;
      });
      return updated;
    });
  };

  const confirmedCount = Object.values(selectedMatches).filter(m => m?.food).length;

  // Theming
  const theme = isParenteral
    ? {
        btnBg: 'bg-sky-600 hover:bg-sky-700',
        headerGrad: 'from-sky-600 to-blue-700',
        activeBadge: 'bg-sky-600 text-white',
        lightBg: 'bg-sky-50',
        lightBorder: 'border-sky-100',
        accentText: 'text-sky-800',
        pulseBorder: 'border-sky-400',
        pingBg: 'bg-sky-100',
        iconText: 'text-sky-600',
        hoverBg: 'hover:bg-sky-50',
        label: 'Produk Parenteral',
      }
    : {
        btnBg: 'bg-violet-600 hover:bg-violet-700',
        headerGrad: 'from-violet-600 to-purple-700',
        activeBadge: 'bg-violet-600 text-white',
        lightBg: 'bg-violet-50',
        lightBorder: 'border-violet-100',
        accentText: 'text-violet-800',
        pulseBorder: 'border-violet-400',
        pingBg: 'bg-violet-100',
        iconText: 'text-violet-600',
        hoverBg: 'hover:bg-violet-50',
        label: 'Makanan Oral/Enteral',
      };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        title={`Input ${theme.label} via Suara`}
        className={`flex items-center gap-1.5 px-3 py-1.5 ${theme.btnBg} text-white text-xs font-semibold rounded-lg shadow-xs transition-all hover:shadow-sm active:scale-95 h-9 sm:h-8 ${className}`}
      >
        <Mic className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Input Suara</span>
        <span className="sm:hidden">Suara</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Panel */}
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className={`bg-gradient-to-r ${theme.headerGrad} px-5 py-4 flex items-center gap-3 flex-shrink-0`}>
          <div className={`w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 ${isListening ? 'animate-pulse' : ''}`}>
            {isListening ? (
              <Mic className="h-5 w-5 text-white animate-bounce" />
            ) : isParenteral ? (
              <Beaker className="h-5 w-5 text-white" />
            ) : (
              <Volume2 className="h-5 w-5 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-white">
              {isParenteral ? 'Input Produk Parenteral via Suara' : 'Input Recall via Suara'}
            </h2>
            <p className="text-[11px] text-white/80 truncate">
              {phase === 'idle' && (isParenteral ? 'Ucapkan nama cairan/infus, jumlah, dan satuan' : 'Ucapkan nama makanan, jumlah, dan satuan')}
              {phase === 'listening' && 'Mendengarkan... Ucapkan sekarang'}
              {phase === 'review' && `${parsedItems.length} item terdeteksi — periksa & konfirmasi`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-100 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 font-medium">{error}</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {/* Phase: Idle or Listening */}
          {(phase === 'idle' || phase === 'listening') && (
            <div className="p-5 space-y-4">
              {/* Contoh ucapan */}
              {phase === 'idle' && (
                <div className={`${theme.lightBg} rounded-xl p-3.5 border ${theme.lightBorder}`}>
                  <p className={`text-xs font-semibold ${theme.accentText} mb-2 flex items-center gap-1.5`}>
                    <Volume2 className="h-3.5 w-3.5" /> Contoh Ucapan:
                  </p>
                  <div className={`space-y-1.5 text-xs ${theme.accentText} font-medium`}>
                    {isParenteral ? (
                      <>
                        <p className="bg-white/80 rounded-lg px-2.5 py-1.5 border border-sky-100 shadow-2xs">
                          "aminofluid 1 botol dan clinimix satu bag"
                        </p>
                        <p className="bg-white/80 rounded-lg px-2.5 py-1.5 border border-sky-100 shadow-2xs">
                          "triofusin 500 satu botol"
                        </p>
                        <p className="bg-white/80 rounded-lg px-2.5 py-1.5 border border-sky-100 shadow-2xs">
                          "ka en 3b satu kolf, otsu d10 500 ml"
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="bg-white/70 rounded-lg px-2.5 py-1.5 border border-violet-100 shadow-2xs">
                          "nasi putih satu porsi dan telur goreng dua butir"
                        </p>
                        <p className="bg-white/70 rounded-lg px-2.5 py-1.5 border border-violet-100 shadow-2xs">
                          "susu peptamen 1 botol, roti gandum 2 lembar"
                        </p>
                        <p className="bg-white/70 rounded-lg px-2.5 py-1.5 border border-violet-100 shadow-2xs">
                          "bubur ayam setengah porsi"
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Listening animation */}
              {phase === 'listening' && (
                <div className="flex flex-col items-center justify-center py-6 space-y-4">
                  <div className="relative">
                    <div className={`w-20 h-20 rounded-full ${theme.pingBg} flex items-center justify-center`}>
                      <Mic className={`h-10 w-10 ${theme.iconText}`} />
                    </div>
                    <span className={`absolute inset-0 rounded-full border-4 ${theme.pulseBorder} animate-ping opacity-40`} />
                  </div>
                  <p className="text-sm font-semibold text-slate-700">Sedang mendengarkan...</p>
                  <p className="text-xs text-slate-400 text-center">
                    {isParenteral ? (
                      <>Ucapkan produk cairan infus, jumlah, dan satuan<br />Contoh: <em>"aminofluid satu botol"</em></>
                    ) : (
                      <>Ucapkan makanan, jumlah, dan satuan<br />Contoh: <em>"nasi putih satu porsi"</em></>
                    )}
                  </p>
                </div>
              )}

              {/* Live transcript */}
              {(transcript || interimTranscript) && (
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <p className="text-[11px] font-semibold text-slate-500 mb-1 uppercase tracking-wide">Transkripsi:</p>
                  <p className="text-sm text-slate-800 font-medium">
                    {transcript}
                    {interimTranscript && <span className="text-slate-400 italic"> {interimTranscript}</span>}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Phase: Review */}
          {phase === 'review' && (
            <div className="p-4 space-y-3">
              {loadingItems && (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Mencocokkan dengan database {isParenteral ? 'parenteral' : 'makanan'}...</span>
                </div>
              )}

              {/* Transcript display */}
              <div className="bg-slate-50 rounded-xl px-3.5 py-2.5 border border-slate-200">
                <p className="text-[11px] font-semibold text-slate-400 mb-0.5 uppercase tracking-wide">Yang diucapkan:</p>
                <p className="text-xs text-slate-700 italic">"{transcript}"</p>
              </div>

              {parsedItems.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm font-semibold text-slate-600">Tidak ada item yang terdeteksi</p>
                  <p className="text-xs text-slate-400 mt-1">Coba ucapkan lebih jelas atau ulangi</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-600">
                    {parsedItems.length} item terdeteksi — pilih {isParenteral ? 'produk' : 'makanan'} yang sesuai:
                  </p>
                  {parsedItems.map((parsed, idx) => {
                    const matchData = selectedMatches[idx] || { food: null, matches: [] };
                    const { food: selectedItem, matches } = matchData;

                    return (
                      <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
                        {/* Parsed header */}
                        <div className={`flex items-center justify-between px-3.5 py-2.5 ${theme.lightBg} border-b ${theme.lightBorder}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-5 h-5 rounded-full ${theme.activeBadge} text-[10px] font-bold flex items-center justify-center flex-shrink-0`}>
                              {idx + 1}
                            </span>
                            <p className={`text-xs font-bold ${theme.accentText} truncate`}>"{parsed.name}"</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Quantity input */}
                            <input
                              type="number"
                              value={parsed.quantity}
                              onChange={e => updateQty(idx, parseFloat(e.target.value) || 1)}
                              min="0.1"
                              step="0.25"
                              className="w-14 h-7 text-xs text-center border border-slate-300 rounded-lg bg-white font-semibold"
                            />
                            <span className="text-[11px] text-slate-500">{parsed.unit}</span>
                            <button
                              onClick={() => removeItem(idx)}
                              className="p-1 text-slate-400 hover:text-red-500 transition-colors rounded"
                              title="Hapus item"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Matches */}
                        <div className="p-2.5 space-y-1.5">
                          {matches.length === 0 ? (
                            <p className="text-xs text-slate-400 italic px-1">
                              Tidak ada {isParenteral ? 'produk parenteral' : 'makanan'} cocok di database — item ini tidak akan ditambahkan
                            </p>
                          ) : (
                            matches.map((item, fIdx) => {
                              const isSelected = selectedItem && (
                                selectedItem === item ||
                                (selectedItem.id && item.id && selectedItem.id === item.id) ||
                                (selectedItem._id && item._id && selectedItem._id === item._id) ||
                                (selectedItem.name === item.name)
                              );

                              return (
                                <button
                                  key={item.id || item._id || `${item.name}-${fIdx}`}
                                  type="button"
                                  onClick={() => setMatchForItem(idx, item)}
                                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-all text-xs border ${
                                    isSelected
                                      ? isParenteral
                                        ? 'bg-sky-600 text-white border-sky-600 shadow-xs font-semibold'
                                        : 'bg-violet-600 text-white border-violet-600 shadow-xs font-semibold'
                                      : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200/90'
                                  }`}
                                >
                                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                    <div
                                      className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 border ${
                                        isSelected
                                          ? 'border-white bg-white'
                                          : 'border-slate-300 bg-slate-50'
                                      }`}
                                    >
                                      {isSelected && (
                                        <div
                                          className={`w-2 h-2 rounded-full ${
                                            isParenteral ? 'bg-sky-600' : 'bg-violet-600'
                                          }`}
                                        />
                                      )}
                                    </div>
                                    <span className="truncate">{item.name}</span>
                                  </div>

                                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                    <span
                                      className={`text-[11px] ${
                                        isSelected ? 'text-white/90 font-bold' : 'text-slate-500'
                                      }`}
                                    >
                                      {formatNumber(item.macronutrients?.energy_kcal || 0)} kkal/{item.unit || 'unit'}
                                    </span>
                                    {isSelected && (
                                      <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                        <Check className="h-3 w-3 text-white stroke-[2.5]" />
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-4 border-t border-slate-100 bg-white flex-shrink-0 space-y-2.5">
          {phase === 'idle' && (
            <button
              onClick={startListening}
              disabled={!isSpeechSupported || loadingItems}
              className={`w-full flex items-center justify-center gap-2.5 py-3.5 ${theme.btnBg} disabled:bg-slate-300 text-white text-sm font-bold rounded-xl transition-all shadow-xs hover:shadow-md active:scale-[0.98]`}
            >
              <Mic className="h-5 w-5" />
              Mulai Rekam Suara
            </button>
          )}

          {phase === 'listening' && (
            <button
              onClick={stopListening}
              className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-red-500 hover:bg-red-600 text-white text-sm font-bold rounded-xl transition-all shadow-xs animate-pulse"
            >
              <MicOff className="h-5 w-5" />
              Selesai — Proses Sekarang
            </button>
          )}

          {phase === 'review' && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setPhase('idle');
                  setTranscript('');
                  setInterimTranscript('');
                  setParsedItems([]);
                  setSelectedMatches({});
                }}
                className="flex-1 py-3 border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors"
              >
                Ulangi Rekam
              </button>
              <button
                onClick={handleConfirm}
                disabled={confirmedCount === 0}
                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-bold rounded-xl transition-all shadow-xs active:scale-[0.98]"
              >
                <span>Tambahkan {confirmedCount > 0 ? `(${confirmedCount} item)` : ''}</span>
              </button>
            </div>
          )}

          {!isSpeechSupported && (
            <p className="text-[11px] text-red-500 text-center">
              Browser tidak mendukung. Gunakan Chrome atau Edge terbaru.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
